import { Basic } from "../basic.js";
import { toBool } from "../../lib/utils.js";
/**
 * @class MSGauge1
 * @extends Basic
 * @classdesc Стрелочный прибор
 * @see https://echarts.apache.org/en/option.html#series-gauge.type
 * */
export class MSGauge1 extends Basic {
    static get observedAttributes() {
        return super.observedAttributes.concat([
            "fontname",
            "fontsize",
            "fontitalic",
            "fontbold",
            "textcolor",
            "value",
            "minvalue",
            "maxvalue",
            "intervalsnumber",
            "unit",
            "intervalticksnumber",
            "arrowcolor",
            "scalecolor",
            "precision",
            "haspercentages",
            "scalefilling",
            "pointerzones",
            "gaugekindvaluestype",
            "hasticks",
            "hassignvalues",
        ]);
    }
    constructor() {
        super();

        this.minvalue = 0;
        this.maxvalue = 100;
        this.intervalsnumber = 10;
        this.intervalticksnumber = 5;
        this.arrowcolor = "#FF0000";
        this.scalecolor = "BLACK";
        this.value = 0;

        this.fontname = "Tahoma";
        this.fontsize = 12;
        this.backgroundcolor = "transparent";
        const shadowRoot = this.attachShadow({ mode: "open" });
        shadowRoot.innerHTML = `<div></div>`;
        this._main = shadowRoot.querySelector("div");

        this._setOptiponsInitGauge();

        this.hasticks = false;
        this.hassignvalues = true;
    }

    get haspercentages() {
        return this._hasPercentages || false;
    }

    set haspercentages(value) {
        this._hasPercentages = toBool(value);
    }

    // Заливка шкалы
    get scalefilling() {
        return this._scaleFilling || "rgb(212,207,212)";
    }

    set scalefilling(value) {
        this._scaleFilling = value;

        if (this.gauge) {
            if (this.pointerzones !== "{}") {
                this.updateAxisLine();
            } else {
                this.gauge.setOption({
                    series: {
                        axisLine: {
                            width: 20,
                            lineStyle: {
                                color: [[1, value]],
                            },
                        },
                    },
                });
            }
        }
    }

    // Аварийные зоны
    get pointerzones() {
        return this._pointerZones || "{}";
    }

    set pointerzones(value) {
        this._pointerZones = value;

        try {
            if (this.gauge) {
                this.updateAxisLine();
            }
        } catch (e) { }
    }

    // Вид значений
    get gaugekindvaluestype() {
        return this._gaugeKindValuesType;
    }

    set gaugekindvaluestype(value) {
        this._gaugeKindValuesType = value;

        switch (+value) {
            case 0: {
                // 0 - Стрелка и значение
                this.gauge.setOption({
                    series: {
                        detail: {
                            show: true,
                            formatter: `{value}${this.unit}`,
                            offsetCenter: ["0%", "20%"],
                        },
                        itemStyle: {
                            opacity: 1,
                        },
                    },
                });
                break;
            }
            case 1: {
                // 1 - Только стрелка
                this.gauge.setOption({
                    series: {
                        detail: {
                            show: false,
                        },
                        itemStyle: {
                            opacity: 1,
                        },
                    },
                });
                break;
            }
            case 2: {
                // 2 - Только значение
                this.gauge.setOption({
                    series: {
                        detail: {
                            show: true,
                            formatter: `{value}${this.unit}`,
                            offsetCenter: ["0%", "20%"],
                        },
                        itemStyle: {
                            opacity: 0,
                        },
                    },
                });
            }
        }
    }

    // Границы интервалов
    get hasticks() {
        return this._hasTicks || false;
    }

    set hasticks(value) {
        this._hasTicks = toBool(value);

        this.gauge.setOption({
            series: {
                splitLine: {
                    show: this._hasTicks,
                },
                axisTick: {
                    show: this._hasTicks,
                },
            },
        });
    }

    // Подписывать значения
    get hassignvalues() {
        return this._hasSignValues;
    }

    set hassignvalues(value) {
        this._hasSignValues = value;

        this.gauge.setOption({
            series: {
                axisLabel: {
                    show: toBool(value),
                },
            },
        });
    }

    get width() {
        return this._width;
    }

    set width(value) {
        this._width = value;
        const tmp = Number(value);
        if (tmp) {
            this.style.width = this._setValueUnit(value);
            this._resizeGauge();
        }
    }

    get height() {
        return this._height;
    }

    set height(value) {
        this._height = value;
        const tmp = Number(value);
        if (tmp) {
            this.style.height = this._setValueUnit(value);
            this._resizeGauge();
        }
    }

    get backgroundcolor() {
        return this._backgroundcolor;
    }

    set backgroundcolor(value) {
        if (value && typeof value === "string") {
            this._backgroundcolor = value;
            this.style.background = this._calcGradient(this.tryToGetUserColor(value));
        }
    }

    get precision() {
        return this._precision;
    }

    set precision(value) {
        value = parseInt(value, 10);
        if (value) {
            this._precision = value;

            this.gauge.setOption({
                series: {
                    data: [
                        {
                            value: this.formatData(this._value),
                        },
                    ],
                    axisLabel: {
                        formatter: (value) => {
                            return this.formatData(value);
                        },
                    },
                },
            });
        }
    }

    get value() {
        return this._value;
    }

    set value(value) {
        if (this._isNumeric(value)) {
            this._value = Number(value);
            if (this.gauge) {
                this.gauge.setOption({
                    series: {
                        data: [
                            {
                                value: this.formatData(this._value),
                            },
                        ],
                    },
                });
            }
        }
    }

    get minvalue() {
        return this._minValue;
    }

    set minvalue(value) {
        if (this._isNumeric(value)) {
            this._minValue = parseInt(value, 10);
            if (this.gauge) {
                this.gauge.setOption({ series: { min: this._minValue } });
                this.updateAxisLine();
            }
        }
    }

    get maxvalue() {
        return this._maxValue;
    }

    set maxvalue(value) {
        if (this._isNumeric(value)) {
            this._maxValue = parseInt(value, 10);
            if (this.gauge) {
                this.gauge.setOption({ series: { max: this._maxValue } });
                this.updateAxisLine();
            }
        }
    }

    get intervalsnumber() {
        return this._intervalsnumber;
    }

    set intervalsnumber(value) {
        if (this._isNumeric(value)) {
            this._intervalsnumber = parseInt(value, 10);
            if (this.gauge) {
                this.gauge.setOption({
                    series: { splitNumber: this._intervalsnumber },
                });
            }
        }
    }

    get unit() {
        return this._unit || "";
    }

    set unit(value) {
        this._unit = value;

        if (this.gauge) {
            this.gauge.setOption({
                series: {
                    detail: {
                        formatter: `{value}${value}`,
                    },
                },
            });
        }
    }

    get intervalticksnumber() {
        return this._intervalticksnumber;
    }

    set intervalticksnumber(value) {
        if (value) {
            this._intervalticksnumber = parseInt(value);
            if (this.gauge) {
                this.gauge.setOption({
                    series: {
                        axisTick: { splitNumber: this._intervalticksnumber },
                    },
                });
            }
        }
    }

    get arrowcolor() {
        return this._arrowcolor;
    }

    set arrowcolor(value) {
        if (value) {
            this._arrowcolor = value;
            if (this.gauge) {
                this.gauge.setOption({
                    series: {
                        itemStyle: { normal: { color: this._arrowcolor } },
                    },
                });
            }
        }
    }

    get scalecolor() {
        return this._scalecolor;
    }

    set scalecolor(value) {
        // Цвет шкалы только для делений
        if (value) {
            this._scalecolor = value;
            if (this.gauge) {
                this.gauge.setOption({
                    series: {
                        axisTick: {
                            lineStyle: {
                                color: value,
                            },
                        },
                        splitLine: {
                            lineStyle: {
                                color: value,
                            },
                        },
                        axisLabel: {
                            color: value,
                        },
                    },
                });
            }
        }
    }

    get fontname() {
        return this._fontname;
    }

    set fontname(value) {
        if (value) {
            this._fontname = value;
            if (this.gauge) {
                this.gauge.setOption({
                    series: { detail: { fontFamily: value } },
                });
            }
        }
    }

    get fontsize() {
        return this._fontsize;
    }

    set fontsize(value) {
        value = parseInt(value, 10);
        if (value) {
            this._fontsize = value;
            if (this.gauge) {
                this.gauge.setOption({
                    series: {
                        detail: { fontSize: value },
                    },
                });
            }
        }
    }

    get fontitalic() {
        return this._fontitalic;
    }

    set fontitalic(value) {
        this._fontitalic = this._toBool(value);
        if (this.gauge) {
            this.gauge.setOption({
                series: {
                    detail: {
                        fontStyle: this._fontitalic ? "italic" : "normal",
                    },
                },
            });
        }
    }

    get fontbold() {
        return this._fontbold;
    }

    set fontbold(value) {
        this._fontbold = this._toBool(value);
        if (this.gauge) {
            this.gauge.setOption({
                series: {
                    detail: { fontWeight: this._fontbold ? "bold" : "normal" },
                },
            });
        }
    }

    get textcolor() {
        return this._textcolor;
    }

    set textcolor(value) {
        if (value) {
            this._textcolor = value;
            if (this.gauge) {
                this.gauge.setOption({
                    series: {
                        detail: { color: this.tryToGetUserColor(value) },
                    },
                });
            }
        }
    }

    updateAxisLine() {
        if (this.pointerzones === "{}") {
            return;
        }
        const diff = this.maxvalue - this.minvalue;
        const values = Object.values(JSON.parse(this.pointerzones));

        this.gauge.setOption({
            series: {
                axisLabel: {
                    distance: this.width / 50,
                    fontSize: this.width / 20,
                },
                axisLine: {
                    lineStyle: {
                        width: 20,
                        color: values
                            .slice(0)
                            .sort((a, b) => (a.value > b.value ? 1 : -1))
                            .map(({ value, color }) => [
                                this.haspercentages
                                    ? value / 100
                                    : (value - this.minvalue) / diff,
                                color,
                            ])
                            .reduce((acc, cur, i, arr) => {
                                if (i === arr.length - 1 && cur.value !== 100) {
                                    return [
                                        ...acc,
                                        cur,
                                        [1, this.scalefilling],
                                    ];
                                }

                                if (i === 0) {
                                    return [cur];
                                }

                                return [...acc, cur];
                            }, []),
                    },
                },
            },
        });
    }

    _setOptiponsInitGauge() {
        this.gauge = echarts.init(this._main, null, {
            width: this.attributes.width.value,
            height: this.attributes.height.value,
        });

        this.gauge.setOption({
            series: [
                {
                    itemStyle: { normal: { color: this.arrowcolor } },
                    type: "gauge",
                    min: 0,
                    max: 100,
                    radius: "100%",
                    center: ["50%", "65%"], // нужно для выравнивания компонента посередине по вертикали
                    startAngle: 180,
                    endAngle: 0,
                    axisLine: {
                        lineStyle: {
                            width: 20,
                            color: [
                                [0, this.scalefilling],
                                [1, this.scalefilling],
                            ],
                        },
                    },
                    axisLabel: {
                        formatter: (value) => {
                            return this.formatData(value);
                        },
                    },
                },
            ],
        });
    }

    formatData(value) {
        const fixed = value.toFixed(this.precision);
        const strVal = value.toString();
        return strVal.length < fixed.length ? value : parseFloat(fixed);
    }

    _resizeGauge() {
        if (this.gauge) {
            this.gauge.resize({
                width: parseFloat(this.width),
                height: parseFloat(this.height),
                series: {
                    axisLabel: {
                        distance: this.width / 50,
                        fontSize: this.width / 20,
                    },
                },
            });
        }
    }

    attributeChangedCallback(attrName, oldVal, newVal) {
        if (oldVal !== newVal) {
            if (
                attrName.startsWith("pointerzones") &&
                !attrName.endsWith("pointerzones")
            ) {
                this.updatePointerZones(attrName, newVal);
            } else {
                this[attrName] = newVal;
            }
            if (this.prop_link[attrName]) {
                this.prop_link[attrName](
                    this.pId ? this.pId + "/" + this.id : this.id,
                    this.Links[attrName],
                    newVal,
                    attrName,
                    this.ItemIndex
                );
            }
        }
    }

    updatePointerZones(attrName, newVal) {
        const [, index, param] = attrName.split(".");
        const pointerZonesObj = JSON.parse(this.pointerzones);
        const key = Object.keys(pointerZonesObj)[index];
        pointerZonesObj[key][param] = newVal;
        this.pointerzones = JSON.stringify(pointerZonesObj);
    }
}
